/**
 * Combat: classic rounds (ADR-0048).
 *
 * A fight is a series of rounds. Each round the attacker lands a hit with probability
 * A / (A + D) — its attack against the defender's modified defence — and otherwise
 * takes one. A hit costs `combat.damagePerRound` hit points. The first side to reach
 * zero dies. Nothing else: no hidden modifiers, no retreat, no luck that the odds do
 * not already include.
 *
 * That is what makes the odds shown before an attack EXACT rather than an estimate.
 * `winChance` is the closed form of the same process `fight` plays out with dice, from
 * the same numbers, so the figure the player sees is the figure the game rolls
 * against. Players forgive a 70% attack that fails; they do not forgive one that was
 * really 40%.
 */

import type { Content, UnitDef } from '../../../content/schema/index.js';
import { cityAt } from '../cities/index.js';
import { nextFloat, type RngState } from '../rng/index.js';
import type { GameState, Unit } from '../state/types.js';
import { allUnits, unitDef, unitIndex } from '../units/index.js';

export const canAttack = (def: UnitDef): boolean => def.attack > 0;
/** Built for walls: a city's defences do not count against its attacks (ADR-0064). */
export const isSiege = (def: UnitDef | undefined): boolean => def?.abilities.includes('siege') === true;
export const canDefend = (def: UnitDef): boolean => def.defence > 0;

/** One reason a defender is stronger than its bare defence, for the odds breakdown. */
export interface DefenceFactor {
  label: string;
  percent: number;
}

/** The walls a city has built, as a percentage (ADR-0064). */
export function wallsOf(content: Content, city: { buildings: readonly number[] }): number {
  let total = 0;
  for (const id of city.buildings) total += content.buildings.get(id).effects.wallsDefence ?? 0;
  return total;
}

/**
 * Everything that strengthens a unit defending this tile, as separate lines.
 *
 * A unit in a city always counts as fortified — it is behind walls whether or not it
 * was told to dig in — which is the one bonus a player could otherwise forget to take.
 * Against a siege unit (`attacker`) none of a city's defences count: not the city, its
 * size, its walls, nor the fortification they give (ADR-0064). Terrain still does.
 */
export function defenceFactors(state: GameState, content: Content, unit: Unit, index: number, attacker?: UnitDef): DefenceFactor[] {
  const rules = content.rules.combat;
  const factors: DefenceFactor[] = [];
  const terrain = content.terrain.get(state.map.terrain[index]!);
  const feature = content.features.get(state.map.feature[index]!);
  if (terrain.defenceBonus > 0) factors.push({ label: terrain.name, percent: terrain.defenceBonus });
  if (feature.defenceBonus > 0) factors.push({ label: feature.name, percent: feature.defenceBonus });
  if (state.map.rivers[index] !== 0 && rules.riverBonus > 0) factors.push({ label: 'River', percent: rules.riverBonus });

  const city = cityAt(state, state.map, index);
  if (city && isSiege(attacker)) {
    factors.push({ label: 'City defences — no use against siege', percent: 0 });
    return factors;
  }
  if (city) {
    factors.push({ label: 'City', percent: rules.cityBonus });
    if (city.population >= rules.largeCitySize && rules.largeCityBonus > 0) {
      factors.push({ label: 'Large city', percent: rules.largeCityBonus });
    }
    // Walls stop soldiers, not bombs (ADR-0065).
    const walls = attacker?.domain === 'air' ? 0 : wallsOf(content, city);
    if (walls > 0) factors.push({ label: 'Walls', percent: walls });
  }
  if (city || unit.orders?.kind === 'fortify') factors.push({ label: 'Fortified', percent: rules.fortifyBonus });
  return factors;
}

/** A unit's defence on a tile, with every bonus applied. */
export function defenceStrength(state: GameState, content: Content, unit: Unit, index: number, attacker?: UnitDef): number {
  const def = unitDef(content, unit);
  let percent = 100;
  for (const factor of defenceFactors(state, content, unit, index, attacker)) percent += factor.percent;
  return (def.defence * percent) / 100;
}

/**
 * Who defends a tile against this attacker: the unit with the best chance of winning,
 * which is strength and health together — a fresh spearman is a better bet than a
 * pikeman on his last legs. Ties to the oldest unit, so the choice is reproducible.
 *
 * Only units that can defend, and only the attacker's enemies' — whose war it is the
 * caller has already checked.
 */
export function bestDefender(state: GameState, content: Content, attacker: Unit, index: number): Unit | null {
  let best: Unit | null = null;
  let bestOdds = 2;
  const attackerDef = unitDef(content, attacker);
  const attack = attackerDef.attack;
  // Ships defend the sea and soldiers the land: a fleet in harbour does not man the
  // walls, and an army aboard a transport cannot fight off a warship.
  const domain = content.terrain.get(state.map.terrain[index]!).water ? 'sea' : 'land';
  for (const unit of allUnits(state)) {
    if (unit.owner === attacker.owner) continue;
    if (unitIndex(state.map, unit) !== index) continue;
    const def = unitDef(content, unit);
    if (!canDefend(def) || def.domain !== domain) continue;
    // The defender is whoever leaves the attacker the worst odds.
    const odds = winChance(
      roundChance(attack, defenceStrength(state, content, unit, index, attackerDef)),
      hitsToKill(content, attacker.hp),
      hitsToKill(content, unit.hp),
    );
    if (odds < bestOdds) {
      bestOdds = odds;
      best = unit;
    }
  }
  return best;
}

/** Rounds a unit with this many hit points can lose before it dies. */
export function hitsToKill(content: Content, hp: number): number {
  return Math.max(1, Math.ceil(hp / content.rules.combat.damagePerRound));
}

/** The attacker's chance of winning any single round. */
export function roundChance(attack: number, defence: number): number {
  if (attack <= 0) return 0;
  if (defence <= 0) return 1;
  return attack / (attack + defence);
}

/**
 * The attacker's exact chance of winning the whole fight: of landing `defenderHits`
 * hits before taking `attackerHits`.
 *
 * A small table rather than the binomial sum: f(i, j) is the chance of winning with i
 * hits still to absorb and j still to land, built up from the edges. No powers, so the
 * figure is the same on every machine (ADR-0045's rule for anything the game decides).
 */
export function winChance(p: number, attackerHits: number, defenderHits: number): number {
  if (p >= 1) return 1;
  if (p <= 0) return 0;
  const q = 1 - p;
  // row[j] holds f(i, j) for the current i; f(0, j) = 0 for j > 0, f(i, 0) = 1.
  let previous = new Float64Array(defenderHits + 1);
  for (let i = 1; i <= attackerHits; i++) {
    const row = new Float64Array(defenderHits + 1);
    row[0] = 1;
    for (let j = 1; j <= defenderHits; j++) row[j] = p * row[j - 1]! + q * previous[j]!;
    previous = row;
  }
  return previous[defenderHits]!;
}

/** Everything about a prospective attack: who defends, at what strength, and the odds. */
export interface Matchup {
  attacker: Unit;
  defender: Unit;
  attack: number;
  defence: number;
  /** The defender's bare defence, before the factors. */
  baseDefence: number;
  factors: DefenceFactor[];
  /** Chance the attacker wins, 0–1. */
  odds: number;
}

/** The fight this attacker would get on this tile, or null if nothing there defends. */
export function matchup(state: GameState, content: Content, attacker: Unit, index: number): Matchup | null {
  const defender = bestDefender(state, content, attacker, index);
  if (!defender) return null;
  const attackerDef = unitDef(content, attacker);
  const attack = attackerDef.attack;
  const defence = defenceStrength(state, content, defender, index, attackerDef);
  return {
    attacker,
    defender,
    attack,
    defence,
    baseDefence: unitDef(content, defender).defence,
    factors: defenceFactors(state, content, defender, index, attackerDef),
    odds: winChance(roundChance(attack, defence), hitsToKill(content, attacker.hp), hitsToKill(content, defender.hp)),
  };
}

export interface FightResult {
  attackerWon: boolean;
  attackerHp: number;
  defenderHp: number;
  rounds: number;
}

/**
 * Play the fight out, round by round, on the combat stream. Mutates nothing: the
 * caller applies the result, so the rules about who dies live in one place.
 */
export function fight(content: Content, m: Matchup, rng: RngState): FightResult {
  const damage = content.rules.combat.damagePerRound;
  const p = roundChance(m.attack, m.defence);
  let attackerHp = m.attacker.hp;
  let defenderHp = m.defender.hp;
  let rounds = 0;
  while (attackerHp > 0 && defenderHp > 0) {
    rounds++;
    if (nextFloat(rng) < p) defenderHp -= damage;
    else attackerHp -= damage;
  }
  return {
    attackerWon: defenderHp <= 0,
    attackerHp: Math.max(0, attackerHp),
    defenderHp: Math.max(0, defenderHp),
    rounds,
  };
}

// ─── bombardment (ADR-0064) ──────────────────────────────────────────────────

/** A prospective bombardment: who is hit, how hard, and what to expect. */
export interface Barrage {
  attacker: Unit;
  target: Unit;
  strength: number;
  defence: number;
  shots: number;
  /** Chance each shot lands. */
  chance: number;
  /** Hit points it can take at most: the target never drops below combat.bombardMinHp. */
  cap: number;
  /** The damage to expect, averaged over every way the shots can fall. */
  expected: number;
}

/**
 * What this unit's guns would do to the enemy on a tile, or null when it has none or
 * there is no one there worth firing at. The target is the defender who would suffer
 * least — the same choice an attack faces — among those not already as low as a
 * bombardment can leave them. The caller checks range and war.
 */
export function barrage(state: GameState, content: Content, attacker: Unit, index: number): Barrage | null {
  const def = unitDef(content, attacker);
  if (!def.bombard) return null;
  const { strength, shots } = def.bombard;
  const minHp = content.rules.combat.bombardMinHp;
  const damage = content.rules.combat.damagePerRound;
  const domain = content.terrain.get(state.map.terrain[index]!).water ? 'sea' : 'land';
  let best: Barrage | null = null;
  for (const unit of allUnits(state)) {
    if (unit.owner === attacker.owner || unitIndex(state.map, unit) !== index || unit.hp <= minHp) continue;
    const target = unitDef(content, unit);
    if (!canDefend(target) || target.domain !== domain) continue;
    const defence = defenceStrength(state, content, unit, index, def);
    const chance = roundChance(strength, defence);
    const cap = unit.hp - minHp;
    let expected = 0;
    // The binomial by repeated multiplication: no powers, the same figure everywhere.
    for (let hits = 0; hits <= shots; hits++) {
      let p = 1;
      for (let i = 0; i < hits; i++) p *= (chance * (shots - i)) / (i + 1);
      for (let i = 0; i < shots - hits; i++) p *= 1 - chance;
      expected += p * Math.min(cap, hits * damage);
    }
    if (!best || expected < best.expected || (expected === best.expected && unit.id < best.target.id)) {
      best = { attacker, target: unit, strength, defence, shots, chance, cap, expected };
    }
  }
  return best;
}

/** Fire the shots on the combat stream. Returns the damage done; mutates nothing. */
export function fireBarrage(content: Content, b: Barrage, rng: RngState): { hits: number; damage: number } {
  let hits = 0;
  for (let i = 0; i < b.shots; i++) if (nextFloat(rng) < b.chance) hits++;
  return { hits, damage: Math.min(b.cap, hits * content.rules.combat.damagePerRound) };
}
